import axios from 'axios';
import { env } from '@/lib/env';
import { useAuthStore } from '@/state/authStore';
import { installPreviewApiAdapter } from '@/preview/apiAdapter';
import { refreshTokens } from '@/features/auth/cognitoAuth';
import type { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';

export const api = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

if (env.VITE_UI_PREVIEW) {
  installPreviewApiAdapter(api);
}

type RetriableRequestConfig = AxiosRequestConfig & { _retry?: boolean };

let refreshPromise: Promise<{ access_token: string; id_token: string }> | null = null;

function setAuthorizationHeader(
  config: AxiosRequestConfig | InternalAxiosRequestConfig,
  token: string,
) {
  const value = `Bearer ${token}`;
  const headers = config.headers as
    | { set?: (name: string, value: string) => void }
    | Record<string, string>
    | undefined;
  if (headers && typeof headers.set === 'function') {
    headers.set('Authorization', value);
  } else {
    config.headers = { ...(headers as Record<string, string> | undefined), Authorization: value };
  }
}

function redirectToLogin() {
  useAuthStore.getState().clear();
  window.location.href = '/login';
}

const validationFieldLabels: Record<string, string> = {
  email: 'メールアドレス',
  name: '名前',
  is_feedbacker: 'フィードバッカー権限',
  is_admin: '管理者権限',
  default_reviewer_id: 'デフォルトフィードバッカー',
};

function formatValidationMessage(message: string) {
  if (message === 'value is not a valid email address') {
    return 'メールアドレスの形式で入力してください';
  }
  if (message === 'Field required') {
    return '入力してください';
  }
  if (message === 'String should have at least 1 character') {
    return '入力してください';
  }
  return message;
}

function formatValidationDetail(detail: unknown): string | null {
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const payload = detail as { message?: unknown; code?: unknown };
    if (typeof payload.message === 'string') return payload.message;
    if (typeof payload.code === 'string') return payload.code;
  }
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const issue = item as { loc?: unknown; msg?: unknown };
        if (typeof issue.msg !== 'string') return null;
        const loc = Array.isArray(issue.loc) ? issue.loc : [];
        const locParts = loc.map((part) => String(part));
        let field: string | undefined;
        for (let i = locParts.length - 1; i >= 0; i -= 1) {
          const part = locParts[i];
          if (part !== 'body' && Number.isNaN(Number(part))) {
            field = part;
            break;
          }
        }
        const label = field ? validationFieldLabels[field] ?? field : null;
        const message = formatValidationMessage(issue.msg);
        return label ? `${label}: ${message}` : message;
      })
      .filter((message): message is string => Boolean(message));
    if (messages.length > 0) return messages.join('\n');
  }
  return null;
}

// Request interceptor: JWT 付与
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    setAuthorizationHeader(config, token);
  }
  return config;
});

// Response interceptor: 401 で強制ログアウト + 5xx/network 統一メッセージ
api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const status = error.response?.status;
    if (status === 401) {
      const originalRequest = error.config as RetriableRequestConfig | undefined;
      const refreshToken = useAuthStore.getState().refreshToken;
      if (originalRequest && !originalRequest._retry && refreshToken) {
        originalRequest._retry = true;
        try {
          refreshPromise ??= refreshTokens(refreshToken);
          const tokens = await refreshPromise;
          refreshPromise = null;
          useAuthStore.getState().updateTokens(tokens);
          setAuthorizationHeader(originalRequest, tokens.access_token);
          return api(originalRequest);
        } catch (refreshError) {
          refreshPromise = null;
          redirectToLogin();
          return Promise.reject(refreshError);
        }
      }
      redirectToLogin();
    } else if (status >= 500) {
      error.userMessage = `サーバーエラーが発生しました (HTTP ${status})。しばらくしてから再度お試しください。`;
      console.error('[API 5xx]', error.config?.url, status, error.response?.data);
    } else if (!error.response) {
      error.userMessage = 'ネットワークエラーが発生しました。接続をご確認ください。';
      console.error('[API network]', error.config?.url, error.message);
    } else if (status >= 400) {
      const data = error.response?.data;
      error.userMessage =
        formatValidationDetail(data?.detail) ||
        (typeof data?.message === 'string' ? data.message : null) ||
        `リクエストエラー (HTTP ${status})`;
    }
    return Promise.reject(error);
  },
);
